import React, { useState, useRef, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';

interface Milestone {
  id: number;
  date: string;
  title: string;
  description: string;
  location: string;
  image: string;
  details: string;
}

const OurStory: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const carouselRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const milestones: Milestone[] = [
    {
      id: 1,
      date: '2010-2015',
      title: 'High School Days',
      description: 'Classmates but strangers',
      location: 'High School',
      image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      details: 'We were in the same high school for 5 years, but we never really talked. We were just classmates passing by in the hallways, unaware that our paths would cross again in the most beautiful way years later.'
    },
    {
      id: 2,
      date: '2017',
      title: 'Noor\'s Journey to Canada',
      description: 'Starting a new chapter',
      location: 'Canada',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      details: 'Noor moved to Canada in 2017, starting a new adventure in a new country. Little did he know that this move would eventually lead him back to someone from his past, but in a completely different way.'
    },
    {
      id: 3,
      date: '2018',
      title: 'Priya\'s Arrival',
      description: 'Following her dreams to Canada',
      location: 'Canada',
      image: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      details: 'Priya arrived in Canada in 2018, pursuing her dreams of becoming a nurse and eventually a doctor. Destiny was about to bring two old classmates together in this new land.'
    },
    {
      id: 4,
      date: '2019',
      title: 'The Reconnection',
      description: 'Fate brings them together',
      location: 'Social Media',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      details: 'In 2019, they reconnected through social media. What started as a simple "Hey, I remember you from high school" turned into hours of conversation, discovering how much they had both grown and changed.'
    },
    {
      id: 5,
      date: '2020',
      title: 'The Proposal',
      description: 'Noor asks Priya to be his girlfriend',
      location: 'Video Call',
      image: 'https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      details: 'After months of getting to know each other again, Noor finally gathered the courage to ask Priya to be his girlfriend during a video call. It was the beginning of their beautiful relationship.'
    },
    {
      id: 6,
      date: '2021',
      title: 'Growing Together',
      description: 'Building their relationship',
      location: 'Long Distance',
      image: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      details: 'Despite the challenges of long-distance, their love grew stronger. They supported each other through their studies, career goals, and personal growth, proving that true love knows no boundaries.'
    },
    {
      id: 7,
      date: '2022',
      title: 'The Silent Period',
      description: 'Two years of reflection',
      location: 'Separate Paths',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      details: 'Sometimes love needs space to grow. For two years, they focused on their individual journeys, but their hearts never forgot the connection they had found.'
    },
    {
      id: 8,
      date: '2024',
      title: 'The Reunion',
      description: 'Love finds its way back',
      location: 'Destiny',
      image: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      details: 'In 2024, fate brought them back together. This time, they were ready. The love that had been growing silently in their hearts finally had the chance to bloom fully.'
    },
    {
      id: 9,
      date: '2024',
      title: 'Becoming Fiancés',
      description: 'The official engagement',
      location: 'Special Moment',
      image: 'https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      details: 'Later in 2024, they officially became fiancés. It was a moment of pure joy and confirmation that their love story was meant to be written together.'
    },
    {
      id: 10,
      date: 'January 4, 2026',
      title: 'The Wedding Day',
      description: 'Two hearts become one',
      location: 'Forever',
      image: 'https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      details: 'On January 4, 2026, their love story will reach its beautiful climax as they say "I do" and begin their journey as husband and wife, proving that true love always finds its way.'
    }
  ];

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % milestones.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, milestones.length]);

  // Update carousel position when currentSlide changes
  useEffect(() => {
    const slideWidth = window.innerWidth > 768 ? 600 : window.innerWidth - 40;
    const newX = -currentSlide * slideWidth;
    
    controls.start({
      x: newX,
      transition: { duration: 0.8, ease: "easeInOut" }
    });
  }, [currentSlide, controls]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % milestones.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + milestones.length) % milestones.length);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute inset-0">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-2xl opacity-20"
            style={{ 
              left: `${Math.random() * 100}%`, 
              top: `${Math.random() * 100}%` 
            }}
            animate={{
              y: [-20, -40, -20],
              rotate: [0, 180, 360],
              opacity: [0.1, 0.3, 0.1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              delay: i * 0.3,
              ease: "easeInOut",
            }}
          >
            💕
          </motion.div>
        ))}
      </div>

      {/* Hero Section */}
      <section className="relative py-20 px-4 text-center z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-8xl mb-8"
          >
            💕
          </motion.div>
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">
            Our Love Story
          </h1>
          <p className="text-xl md:text-2xl text-purple-200 max-w-4xl mx-auto mb-12 leading-relaxed">
            A journey that began in high school, took us across continents, 
            and brought us together in the most beautiful way possible
          </p>
        </motion.div>
      </section>

      {/* Horizontal Carousel */}
      <section className="relative py-12 px-4 z-10">
        <div className="max-w-7xl mx-auto">
          {/* Carousel Container */}
          <div className="relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-sm border border-white/20">
            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 backdrop-blur-md rounded-full p-4 hover:bg-white/30 transition-all duration-300"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 backdrop-blur-md rounded-full p-4 hover:bg-white/30 transition-all duration-300"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Auto-play Toggle */}
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="absolute top-4 right-4 z-20 bg-white/20 backdrop-blur-md rounded-full p-3 hover:bg-white/30 transition-all duration-300"
            >
              <span className="text-white text-lg">
                {isAutoPlaying ? '⏸️' : '▶️'}
              </span>
            </button>

            {/* Carousel Slides */}
            <div className="relative h-[700px] md:h-[800px]">
              <motion.div
                ref={carouselRef}
                animate={controls}
                className="flex h-full"
              >
                {milestones.map((milestone, index) => (
                  <div
                    key={milestone.id}
                    className="w-full md:w-[600px] flex-shrink-0 px-4 md:px-8 flex items-center"
                  >
                    {/* Card with Better Layout */}
                    <div className="w-full h-[600px] md:h-[700px] bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md rounded-3xl border border-white/20 shadow-2xl overflow-hidden">
                      {/* Full-width Image Background */}
                      <div className="relative h-[250px] md:h-[300px] w-full">
                        <img
                          src={milestone.image}
                          alt={milestone.title}
                          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                        
                        {/* Date Badge */}
                        <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                          <span className="text-white text-sm font-bold">{milestone.date}</span>
                        </div>
                        
                        {/* Location Badge */}
                        <div className="absolute bottom-4 left-4 bg-black/40 backdrop-blur-sm rounded-full px-3 py-1">
                          <span className="text-white text-xs font-medium">{milestone.location}</span>
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className="h-[350px] md:h-[400px] p-6 md:p-8 flex flex-col justify-between">
                        {/* Title and Description */}
                        <div className="text-center">
                          <motion.h3 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="text-2xl md:text-3xl font-serif font-bold text-white mb-3"
                          >
                            {milestone.title}
                          </motion.h3>
                          
                          <motion.p 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="text-base md:text-lg text-purple-200 mb-6 leading-relaxed font-medium"
                          >
                            {milestone.description}
                          </motion.p>
                        </div>
                        
                        {/* Details Section */}
                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: 0.6 }}
                          className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20 flex-1 flex items-center"
                        >
                          <p className="text-sm md:text-base text-slate-200 leading-relaxed text-center">
                            {milestone.details}
                          </p>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Progress Indicators */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
              {milestones.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? 'bg-white shadow-lg scale-125' 
                      : 'bg-white/50 hover:bg-white/75'
                  }`}
                />
              ))}
            </div>

            {/* Slide Counter */}
            <div className="absolute top-4 left-4 z-20 bg-white/20 backdrop-blur-md rounded-full px-4 py-2">
              <span className="text-white text-sm font-medium">
                {currentSlide + 1} / {milestones.length}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="relative py-20 px-4 z-10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-12 border border-white/20"
          >
            <blockquote className="text-2xl md:text-3xl font-serif italic text-white mb-6 leading-relaxed">
              "The best love stories are not those that are perfect from the start, 
              but those that grow stronger with every challenge and every moment together."
            </blockquote>
            <cite className="text-lg text-purple-200">
              — Noor & Priya
            </cite>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OurStory;